import React, { useContext, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType } from '@unfoldrtech/portal-mic'
import {
  EChannelTypeGlobal,
  ECreativeStatus,
  EReportColumnHeader,
  EReportingTimeWindow,
  IDAReportAdGroupsCreativesTable,
  IDAReportCreativesTable,
  IDOOHReportAdGroupsCreativesTable,
  IDOOHReportCreativesTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { TReportExportType, TReportingFilters } from '../../../../models'
import { AppContext, AdGroupContext, CampaignContext } from '../../../../models/contexts'
import {
  selectCreativesTableReportingFilters,
  selectReportingFilters,
  setIsReportingV3,
} from '../../../../store/reportingFilters'
import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import {
  convertChannelTypeToParam,
  convertEParamChannelTypeToEChannelTypeGlobal,
  generateCorrelationId,
  getTimezone,
} from '../../../../utils/helpers'
import { sanitizeDataTestId } from '../../../../utils'
import { getCreativesEDatasetKey } from '../helpers/get-creatives-e-dataset-key'
import { TDatasetWhitelistKey } from '../../models'
import { ReportingAdGroupsChartWrapper } from './ReportingCreativesChartWrapper'
import { ReportingAdGroupsTableWrapper } from './ReportingCreativesTableWrapper'
import { coreAPIV3 } from '../../../../services/coreAPIV3'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

type IReportCreativesTable =
  | IDAReportCreativesTable
  | IDAReportAdGroupsCreativesTable
  | IDOOHReportCreativesTable
  | IDOOHReportAdGroupsCreativesTable

export const ReportingCreativesTemplate = ({
  channelType: propChannelType,
}: {
  channelType?: EChannelTypeGlobal
}) => {
  const pageTitle = propChannelType
    ? `reporting.header.creatives.${sanitizeDataTestId(propChannelType)}`
    : 'reporting.header.creatives'

  const intl = useIntl()
  const dispatch = useDispatch()

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)
  const { channelType: reportingFilterChannelType } =
    useSelector(selectReportingFilters)
  const filters = useSelector(selectCreativesTableReportingFilters)
  const channelType =
    propChannelType ||
    convertEParamChannelTypeToEChannelTypeGlobal(
      reportingFilterChannelType ||
        convertChannelTypeToParam(campaign.channelType)
    )

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<IReportCreativesTable>()

  const onTableDataChange = (adGroupsTableData: IReportCreativesTable) => {
    setTableData(adGroupsTableData)
  }
  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      getCreativesEDatasetKey(channelType),
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPIV3.advertiserReporting().getAdvertiserCreativesReportTableV3(
              advertiserId,
              generateCorrelationId(),
              retailerId,
              campaign?.id,
              adGroup?.id,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as unknown as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status as ECreativeStatus | undefined
            )
          : await coreAPIV3.retailerReporting().getRetailerCreativesReportTableV3(
              retailerId,
              generateCorrelationId(),
              advertiserId,
              campaign?.id,
              adGroup?.id,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status as ECreativeStatus
            )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      <ReportPageTemplateLayout
        title={pageTitle}
        isExportDisabled={isExporting || !tableData?.datasetKey}
        chartWrapper={
          <ReportingAdGroupsChartWrapper channelType={channelType} />
        }
        tableWrapper={
          <ReportingAdGroupsTableWrapper
            onTableDataChange={onTableDataChange}
            channelType={channelType}
          />
        }
        onExportData={onExportData}
      />
      <SuccessToast
        show={showExportCappedToast}
        onClose={() => setShowExportCappedToast(false)}
        translationKey="reporting.export.capped"
      />
      <ErrorToast
        show={showExportErrorToast}
        onClose={() => setShowExportErrorToast(false)}
        translationKey="reporting.export.failed"
      />
    </>
  )
}
